import type { CSSProperties } from 'react';

import { assetUrl } from '../assets/asset-manifest.js';
import type { AssetName } from '../assets/asset-manifest.js';

/**
 * The CSS `border-image` declaration that paints a `*.9.png` frame asset onto
 * an element. The shared brass frames carry a transparent margin and painted
 * corners in their outer ~8px, so slicing there keeps the corners intact at
 * any desktop/mobile size (docs/ART_ASSETS.md §8, docs/UI_GUIDELINES.md §10).
 * `fill` keeps the asset's own centre pixels (the parchment/emerald face) for
 * a filled panel; omit it for a hollow frame drawn over an existing surface.
 */
export function nineSliceStyle(
  asset: AssetName,
  slice = 8,
  fill = true,
  outset = 0,
): CSSProperties {
  return {
    borderStyle: 'solid',
    // `outset` is how much of the asset's slice is transparent margin. The
    // element's own border is only the *painted* remainder, and the frame art
    // is pushed back out over that margin — so the element's background fills
    // right up to where the paint begins (no gap inside the trim) while
    // nothing of that background shows beyond it (no bleed outside the trim).
    borderWidth: `${String(slice - outset)}px`,
    borderImageSource: `url("${assetUrl(asset)}")`,
    borderImageSlice: fill ? `${String(slice)} fill` : String(slice),
    borderImageWidth: `${String(slice)}px`,
    borderImageOutset: `${String(outset)}px`,
    borderImageRepeat: 'stretch',
    imageRendering: 'pixelated',
  };
}
